import React, { useCallback, useRef } from "react";

import {
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignJustify as FormatAlignJustifyIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Videocam as VideocamIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { EditorContent } from "@tiptap/react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { withStyles } from "tss-react/mui";

import { Features } from "@/config/features";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";

import FormElement from "../../store/FormElement";
import { useEditing } from "../utils/formElement";
import { renderDefaultTipTapHTML, useTipTapEditor } from "../utils/helpers";
import {
  StyledDeleteIcon,
  StyledDragIndicatorIcon,
  StyledEditIcon,
  StyledHorizontalDivider,
  StyledInfoIcon,
  useStyles,
} from "../utils/Icons";

const StyledEditorContent = withStyles(EditorContent, {
  root: {
    minHeight: 150,
    cursor: "text",
    border: "1px solid transparent",
    "&:hover": {
      borderColor: "black",
    },
  },
});

type DescriptionArguments = {
  label: string;
  description: string;
};

interface Props {
  element: FormElement<DescriptionArguments>;
}

const FormDescription: React.FC<Props> = observer(({ element }) => {
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const snackbar = useGlobalSnackbar();
  const editor = useTipTapEditor({
    content: element.arguments.description,
    onUpdate({ editor }) {
      element.setArgument("description", editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    const previousUrl = editor?.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor?.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor
      ?.chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }, [editor]);

  const editing = useEditing(element);

  const { classes } = useStyles();

  function handleOpenFile() {
    inputFileRef.current?.click();
  }
  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event?.target?.files?.[0] != null) {
      const file = event.target.files[0];
      const url = URL.createObjectURL(file);

      // Immediately add an image preview to the editor. We'll replace it
      // later after file upload.
      editor?.chain().focus().setImage({ src: url }).run();

      // Reset file input value so we can upload another file.
      event.target.value = "";
      try {
        const blob = await element.uploadMultipleFile(file);
        const baseUrl = import.meta.env.VITE_API_URL;
        const blobUrl = `${baseUrl}/rails/active_storage/blobs/redirect/${blob.signed_id}/${blob.filename}`;
        editor?.state.doc.descendants((node, pos) => {
          if (node.type.name === "image" && node.attrs.src === url) {
            const transaction = editor?.state.tr;
            transaction.setNodeAttribute(pos, "src", blobUrl);
            editor.view.dispatch(transaction);
            return false;
          }
        });
      } catch (error) {
        snackbar.open(displayError(error, "Error: Cannot upload photo."));

        // Remove our preview.
        editor?.state.doc.descendants((node, pos) => {
          if (node.type.name === "image" && node.attrs.src === url) {
            const transaction = editor?.state.tr;
            transaction.delete(pos, pos + 1);
            editor.view.dispatch(transaction);
            return false;
          }
        });
      }
    }
  }

  return (
    <>
      {!editing.value ? (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon
            className={clsx(["styled-drag-indicator"])}
          />
          <div className={classes.columnContent}>
            <Typography variant="h6" color="textSecondary">
              {element.arguments.label || "Description Text Label"}
            </Typography>
            {renderDefaultTipTapHTML(editor, "")}
          </div>

          <div className={clsx([classes.editIcons, "edit-icons-container"])}>
            <StyledInfoIcon />
            <StyledEditIcon onClick={editing.toggle} />
            <StyledDeleteIcon onClick={() => element.remove()} />
          </div>
        </Box>
      ) : (
        <Box className={classes.body}>
          <StyledDragIndicatorIcon />
          <Box className={classes.edit}>
            <Box className={classes.columnContent}>
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginBottom: 8 }}
              >
                Description label
              </Typography>

              <TextField
                value={element.arguments.label}
                onChange={(e) => element.setArgument("label", e.target.value)}
              />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ alignSelf: "flex-start", marginTop: 8 }}
              >
                Description
              </Typography>
              <div className={classes.multilineTextfieldContainer}>
                <div className={classes.textEditor}>
                  <div className={classes.formattingIconsContainer}>
                    <FormatBoldIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]:
                          editor?.isActive("bold"),
                      })}
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                    />
                    <FormatItalicIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]:
                          editor?.isActive("italic"),
                      })}
                      onClick={() =>
                        editor?.chain().focus().toggleItalic().run()
                      }
                    />
                    <FormatUnderlinedIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]:
                          editor?.isActive("underline"),
                      })}
                      onClick={() => editor?.commands.toggleUnderline()}
                    />
                  </div>

                  <div className={classes.formattingIconsContainer}>
                    <FormatAlignLeftIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "left",
                        }),
                      })}
                      onClick={() => editor?.commands.setTextAlign("left")}
                    />
                    <FormatAlignCenterIcon
                      onClick={() => editor?.commands.setTextAlign("center")}
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "center",
                        }),
                      })}
                    />
                    <FormatAlignRightIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "right",
                        }),
                      })}
                      onClick={() => editor?.commands.setTextAlign("right")}
                    />
                    <FormatAlignJustifyIcon
                      onClick={() => editor?.commands.setTextAlign("justify")}
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]: editor?.isActive({
                          textAlign: "justify",
                        }),
                      })}
                    />
                  </div>

                  <div className={classes.formattingIconsContainer}>
                    <FormatListBulletedIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]:
                          editor?.isActive("bulletList"),
                      })}
                      onClick={() =>
                        editor?.chain().focus().toggleBulletList().run()
                      }
                    />
                    <FormatListNumberedIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]:
                          editor?.isActive("orderedList"),
                      })}
                      onClick={() =>
                        editor?.chain().focus().toggleOrderedList().run()
                      }
                    />
                  </div>

                  <div className={classes.formattingIconsContainer}>
                    <ImageIcon
                      className={classes.formattingIcons}
                      onClick={handleOpenFile}
                    />
                    {Features.formVideosEnabled && (
                      <VideocamIcon className={classes.formattingIcons} />
                    )}
                    <LinkIcon
                      className={clsx(classes.formattingIcons, {
                        [classes.formattingIconsActive]:
                          editor?.isActive("link"),
                      })}
                      onClick={setLink}
                    />
                  </div>
                </div>
                <div className={classes.descriptionTextEditor}>
                  <StyledEditorContent
                    className={classes.editorContent}
                    editor={editor}
                  />
                  <input
                    style={{ display: "none" }}
                    ref={inputFileRef}
                    accept="image/*"
                    type="file"
                    onChange={handleFileChange}
                  />
                </div>
              </div>
              <StyledHorizontalDivider orientation="horizontal" />
              <div className={classes.editFooter}>
                <div className={classes.editButtons}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        aria-label="Required"
                        checked={element.required}
                        onChange={(e) => element.setRequired(e.target.checked)}
                        inputProps={{ "aria-label": "primary checkbox" }}
                      />
                    }
                    label="Required"
                  />
                  <Button variant="outlined" onClick={editing.cancel}>
                    Cancel
                  </Button>
                  <Button
                    className={classes.saveChangesButton}
                    onClick={editing.stop}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
});

export default FormDescription;
